import { useState, useCallback } from 'react';

const STORAGE_KEY = 'dextoro_recent_searches';
const MAX_ITEMS = 5;

interface RecentSearch {
  query: string;
  timestamp: number;
}

function readSearches(): RecentSearch[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as RecentSearch[];
  } catch {
    return [];
  }
}

function writeSearches(items: RecentSearch[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Ignore write failures (incognito, storage full)
  }
}

export function useRecentSearches() {
  const [searches, setSearches] = useState<RecentSearch[]>(readSearches);

  const addSearch = useCallback((query: string) => {
    const trimmed = query.trim();
    if (trimmed.length < 2) return;

    setSearches((prev) => {
      const filtered = prev.filter(
        (s) => s.query.toLowerCase() !== trimmed.toLowerCase()
      );
      const next = [{ query: trimmed, timestamp: Date.now() }, ...filtered].slice(
        0,
        MAX_ITEMS
      );
      writeSearches(next);
      return next;
    });
  }, []);

  const removeSearch = useCallback((query: string) => {
    setSearches((prev) => {
      const next = prev.filter(
        (s) => s.query.toLowerCase() !== query.toLowerCase()
      );
      writeSearches(next);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    writeSearches([]);
    setSearches([]);
  }, []);

  return { searches, addSearch, removeSearch, clearAll };
}
